import { useEffect, useRef, useState } from "react";
import { X, Search, Loader2, Send, Users } from "lucide-react";
import api from "../../../../shared/services/api";
import { enviarContatos } from "../../services/mensagensApi";

/**
 * Modal para selecionar e enviar contatos de clientes da empresa via WhatsApp.
 * Multi-select com busca por nome/telefone.
 * Converte o formato interno de clientes para o payload Meta contacts[].
 *
 * @param {{
 *   conversaId: number,
 *   onClose: () => void,
 *   onSent: (mensagem: object) => void,
 * }} props
 */
export default function ContactSender({ conversaId, onClose, onSent }) {
  const [clientes,     setClientes]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [busca,        setBusca]        = useState("");
  const [selecionados, setSelecionados] = useState(new Set());
  const [enviando,     setEnviando]     = useState(false);
  const [erro,         setErro]         = useState(null);
  const modalRef = useRef(null);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    function handleEsc(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    api
      .get("/clientes", { params: { limit: 200 } })
      .then((resp) => {
        if (cancelado) return;
        const lista = resp.data?.data ?? resp.data?.clientes ?? resp.data ?? [];
        setClientes(Array.isArray(lista) ? lista : []);
      })
      .catch(() => { if (!cancelado) setClientes([]); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, []);

  const clientesFiltrados = clientes.filter((c) => {
    const termo = busca.toLowerCase().trim();
    if (!termo) return true;
    return (
      (c.nome     ?? "").toLowerCase().includes(termo) ||
      (c.telefone ?? "").includes(termo)
    );
  });

  function toggleSelecionado(id) {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  function clienteParaMeta(cliente) {
    const contato = { name: { formatted_name: cliente.nome ?? "Contato" } };
    if (cliente.telefone) contato.phones = [{ phone: cliente.telefone, type: "CELL" }];
    if (cliente.email)    contato.emails = [{ email: cliente.email,    type: "WORK" }];
    return contato;
  }

  async function handleEnviar() {
    if (selecionados.size === 0) return;
    const contactsPayload = clientes
      .filter((c) => selecionados.has(c.id))
      .map(clienteParaMeta);

    setEnviando(true);
    setErro(null);
    try {
      const resultado = await enviarContatos(conversaId, contactsPayload);
      onSent(resultado);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.error ?? "Erro ao enviar contatos.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Selecionar contatos para enviar"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-brand-400" aria-hidden="true" />
            Enviar contato
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-ink-4 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Busca + contador */}
        <div className="px-4 pt-3 pb-2 border-b border-white/[0.06] flex-shrink-0">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-5 pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              aria-label="Buscar cliente"
              className="w-full bg-surface-2 border border-white/[0.08] rounded text-xs text-ink-2 pl-8 pr-3 py-1.5 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
            />
          </div>
          {selecionados.size > 0 && (
            <p className="text-xs text-brand-400 mt-1.5" aria-live="polite">
              {selecionados.size} selecionado{selecionados.size > 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Lista */}
        <div
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 min-h-0"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Lista de clientes"
        >
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
            </div>
          ) : clientesFiltrados.length === 0 ? (
            <p className="text-xs text-ink-4 text-center py-8 px-4">
              {busca ? "Nenhum cliente encontrado." : "Nenhum cliente cadastrado."}
            </p>
          ) : (
            clientesFiltrados.map((cliente) => {
              const sel = selecionados.has(cliente.id);
              return (
                <button
                  key={cliente.id}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  onClick={() => toggleSelecionado(cliente.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.06] text-left transition-colors focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-brand-500 ${
                    sel ? "bg-brand-900" : "hover:bg-surface-2"
                  }`}
                >
                  {/* Checkbox visual */}
                  <div
                    className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      sel ? "bg-brand-600 border-brand-600" : "border-white/[0.12]"
                    }`}
                    aria-hidden="true"
                  >
                    {sel && (
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M1.5 5L4 7.5L8.5 2.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Avatar */}
                  <div
                    className="w-7 h-7 rounded-full bg-surface-3 text-ink-2 flex items-center justify-center text-xs font-bold flex-shrink-0 select-none"
                    aria-hidden="true"
                  >
                    {(cliente.nome ?? "?")[0].toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-1 truncate">
                      {cliente.nome ?? "Sem nome"}
                    </p>
                    {cliente.telefone && (
                      <p className="text-xs text-ink-4 truncate">{cliente.telefone}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between flex-shrink-0">
          <div className="flex-1">
            {erro && (
              <p className="text-xs text-red-400 mr-3" role="alert">{erro}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-ink-3 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded px-3 py-1.5"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleEnviar}
              disabled={selecionados.size === 0 || enviando}
              aria-label={`Enviar ${selecionados.size} contato(s) selecionado(s)`}
              className="flex items-center gap-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              {enviando
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
